// components/RoomCode.jsx: M3-10 large room-code display + copy.
//
// The room code is the social primitive (synthesis §6). Display it
// large with extra letter-spacing so it's easy to read aloud, and put
// a one-click copy button next to it. Falls back to a temporary
// success label if the clipboard API isn't available (Safari quirk in
// non-secure contexts).
//
// Phase 3 #7: below the code we also surface a shareable room LINK with a
// primary "Share" button (Web Share API, clipboard fallback) and a
// secondary "Copy" button (copies the raw room URL). The URL itself is
// built + reflected into the address bar by useRoomWs (replaceState); here
// we just derive the same string from the live code so Share/Copy work even
// before the URL-sync effect runs.

import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { roomShareUrl, shareRoom } from '../nav/room-url.js';

/**
 * @param {{ code: string }} props
 */
export function RoomCode({ code }) {
  const [copied, setCopied] = useState(false);
  // #7: status flash for the Share/Copy LINK controls, separate from the
  // code-copy flash above so they don't fight over one label.
  const [linkStatus, setLinkStatus] = useState('');
  /** @type {{current: ReturnType<typeof setTimeout>|null}} */
  const timerRef = useRef(null);
  /** @type {{current: ReturnType<typeof setTimeout>|null}} */
  const linkTimerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current);
  }, []);

  const copy = useCallback(async () => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(code);
      } else if (typeof document !== 'undefined') {
        // Fallback: ephemeral input + execCommand. Works on older
        // browsers but is gated by user-initiated click, fine for
        // this button.
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Surface failure inline so the user knows to copy manually.
      setCopied(false);
    }
  }, [code]);

  // #7: full shareable URL for this room. Null only if the code is somehow
  // unparseable, in which case the Share/Copy row hides.
  const shareUrl = (typeof window !== 'undefined' && window.location)
    ? roomShareUrl(code, window.location.origin)
    : null;

  const flashLink = useCallback((label) => {
    setLinkStatus(label);
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current);
    linkTimerRef.current = setTimeout(() => setLinkStatus(''), 1500);
  }, []);

  // Primary "Share": Web Share API when available, else clipboard fallback.
  const onShare = useCallback(async () => {
    if (!shareUrl) return;
    try {
      const result = await shareRoom(shareUrl, {
        nav: typeof navigator !== 'undefined' ? navigator : undefined,
        clipboard: (typeof navigator !== 'undefined' && navigator.clipboard) || undefined,
      });
      if (result === 'copied') flashLink('Link copied!');
      else if (result === 'shared') flashLink('Shared!');
      // 'cancelled' → no flash (the user backed out deliberately).
    } catch {
      // Best-effort: never throw out of a click handler.
    }
  }, [shareUrl, flashLink]);

  // Secondary "Copy": always copies the raw room URL to the clipboard.
  const onCopyLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(shareUrl);
      } else if (typeof document !== 'undefined') {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
      }
      flashLink('Link copied!');
    } catch {
      flashLink('Copy failed');
    }
  }, [shareUrl, flashLink]);

  // The status line stays mounted so its live region exists before updates
  // and the card keeps the same height when a link is copied.
  return (
    <div class="room-code" data-testid="room-code">
      <span class="room-code-label">Room code</span>
      <code class="room-code-value" data-testid="room-code-value">{code}</code>
      <button
        type="button"
        class="room-code-copy"
        data-testid="room-code-copy"
        onClick={copy}
        aria-label={`Copy room code ${code} to clipboard`}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
      {shareUrl && (
        <button
          type="button"
          class="room-share-btn primary"
          data-testid="room-share"
          onClick={onShare}
          aria-label="Share this room link"
        >
          Share
        </button>
      )}
      {shareUrl && (
        <button
          type="button"
          class="room-share-btn secondary"
          data-testid="room-share-copy"
          onClick={onCopyLink}
          aria-label="Copy this room link to clipboard"
        >
          Copy link
        </button>
      )}
      <span
        class="room-share-status"
        role="status"
        aria-live="polite"
        data-testid="room-share-status"
      >
        {linkStatus}
      </span>
    </div>
  );
}
